import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import type { ExperimentTest } from '../node/experiments'
import type { NavigationCategory } from '../types/navigation'
import trackImpression from '../tracking/trackImpression'

type NavContextType = {
  hoverNavItem: (categoryId: string, isMobile?: boolean) => void
  hoverSubNavItem: (categoryId: string, subcategoryId: string) => void
  isMxNav: boolean
}

const NavContext = createContext<NavContextType>({ hoverNavItem: () => {}, hoverSubNavItem: () => {}, isMxNav: false })

type NavContextProviderProps = {
  children: React.ReactNode
  topLevelNavNodeTests: (string | ExperimentTest)[]
  userTests: ExperimentTest[]
  navigation: NavigationCategory[]
}

export default function NavContextProvider({ children, topLevelNavNodeTests, userTests, navigation }: NavContextProviderProps) {
  const { auth } = useAuth()
  const [impressionQueue, setImpressionQueue] = useState<(string | ExperimentTest)[]>([])

  const triggerImpression = useCallback(
    (tests: (string | ExperimentTest)[], userTests?: ExperimentTest[]) => {
      if (!userTests) return

      setImpressionQueue((prevQueue) =>
        tests.reduce(
          (impressions: (string | ExperimentTest)[], test: string | ExperimentTest) => {
            if (!impressions.includes(test)) {
              impressions.push(test)
            }
            return impressions
          },
          [...prevQueue],
        ),
      )
    },
    [userTests],
  )

  useEffect(() => {
    triggerImpression(topLevelNavNodeTests, userTests)
  }, [topLevelNavNodeTests, userTests])

  useEffect(() => {
    if (auth && userTests?.length && impressionQueue.length) {
      trackImpression(impressionQueue as ExperimentTest[], userTests)
      setImpressionQueue([])
    }
  }, [auth, userTests, impressionQueue])

  const hoverNavItem = useCallback(
    (categoryId: string, isMobile?: boolean) => {
      /* When using keyboard focus events, changing focus to subcategories within the category bubbles
       * event up to the category. That fires this event, but is not actually a state change
       * so ensure it is treated as a "non-change" and doesn't re-render or set incorrect data
       */

      if (!categoryId) return

      const currentNavItem = navigation.find((item) => item.id === categoryId) as NavigationCategory
      let testsInChildren = currentNavItem?.testsInChildren ?? []

      // Text based navigation desktop will show both second and third level nav nodes
      if (!currentNavItem.isVisual && !isMobile && currentNavItem.children) {
        currentNavItem.children.forEach((child) => {
          if (child.testsInChildren) {
            testsInChildren = child.testsInChildren?.reduce((tests, test) => {
              if (!tests.includes(test)) tests.push(test)
              return tests
            }, testsInChildren)
          }
        })
      }

      triggerImpression(testsInChildren, userTests)
    },
    [userTests],
  )

  const hoverSubNavItem = React.useCallback(
    (categoryId: string, subcategoryId: string) => {
      /* When using keyboard focus events, changing focus to links within the subcategory bubbles
       * event up to the subcategory. That fires this event, but is not actually a state change
       * so ensure it is treated as a "non-change" and doesn't re-render
       */

      if (subcategoryId) {
        const currentNavItem = navigation.find((item) => item.id === categoryId)?.children?.find((child) => child.id === subcategoryId)
        const testsInChildren = currentNavItem?.testsInChildren ?? []
        if (testsInChildren.length) {
          triggerImpression(testsInChildren, userTests)
        }
      }
    },
    [userTests, navigation],
  )

  const isMxNav = useMemo(() => navigation?.some((nav) => nav?.children.some((child) => child.startOfMx)), [navigation])

  return <NavContext.Provider value={{ hoverNavItem, hoverSubNavItem, isMxNav }}>{children}</NavContext.Provider>
}

export const useNavContext = () => useContext(NavContext)
